<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

type KpiFormat = 'text' | 'number' | 'currency' | 'percent'
type KpiVariant = 'default' | 'accent'
type KpiTone = 'default' | 'positive' | 'negative'

const toneClasses: Record<KpiTone, string> = {
  default: 'text-ink',
  positive: 'text-success-strong',
  negative: 'text-danger',
}

const props = withDefaults(
  defineProps<{
    title: string
    value?: string | number | null
    subtitle?: string | null
    format?: KpiFormat
    currency?: string
    prefix?: string
    suffix?: string
    minimumFractionDigits?: number
    maximumFractionDigits?: number
    variant?: KpiVariant
    tone?: KpiTone
  }>(),
  {
    value: null,
    subtitle: null,
    format: 'text',
    currency: 'EUR',
    prefix: '',
    suffix: '',
    minimumFractionDigits: undefined,
    maximumFractionDigits: undefined,
    variant: 'default',
    tone: 'default',
  },
)

const { n } = useI18n()

const formattedValue = computed(() => {
  const value = props.value

  if (typeof value !== 'number' || props.format === 'text') {
    return `${props.prefix}${value ?? ''}${props.suffix}`
  }

  const options: Intl.NumberFormatOptions = {
    minimumFractionDigits: props.minimumFractionDigits,
    maximumFractionDigits: props.maximumFractionDigits,
  }

  if (props.format === 'currency') {
    options.style = 'currency'
    options.currency = props.currency
  } else if (props.format === 'percent') {
    options.style = 'percent'
  }

  return `${props.prefix}${n(value, options)}${props.suffix}`
})
</script>

<template>
  <article class="fh-card min-w-0 px-6 py-4" :class="{ 'relative overflow-hidden': variant === 'accent' }">
    <div
      v-if="variant === 'accent'"
      class="absolute inset-x-0 top-0 h-0.75 [background-image:var(--gradient-brand)]"
      aria-hidden="true" />
    <p class="section-label section-label--sm wrap-break-word">{{ title }}</p>
    <p class="mt-1 text-2xl font-bold tracking-tight wrap-break-word tabular-nums" :class="toneClasses[tone]">
      {{ formattedValue }}
    </p>
    <p v-if="subtitle" class="text-ink-3 mt-1 text-xs wrap-break-word">{{ subtitle }}</p>
  </article>
</template>
